/**
 * PixiFreeSpinsOverlay — Badge displayed during free spins showing remaining count and multiplier.
 * Positioned at top-center of the grid area.
 */
import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import gsap from 'gsap';

export class PixiFreeSpinsOverlay {
  readonly container = new Container();

  private bg = new Graphics();
  private titleText: Text;
  private countText: Text;
  private multiplierText: Text;

  constructor() {
    this.container.addChild(this.bg);

    // "FREE SPINS" label
    this.titleText = new Text({
      text: 'FREE SPINS',
      style: new TextStyle({
        fontFamily: 'Inter, Arial, sans-serif',
        fontSize: 10,
        fontWeight: '600',
        fill: '#d8b4fe', // purple-300
        align: 'center',
        letterSpacing: 1,
      }),
    });
    this.titleText.anchor.set(0.5, 0);
    this.container.addChild(this.titleText);

    // Remaining count
    this.countText = new Text({
      text: '0',
      style: new TextStyle({
        fontFamily: 'Inter, Arial, sans-serif',
        fontSize: 18,
        fontWeight: 'bold',
        fill: '#ffffff',
        align: 'center',
      }),
    });
    this.countText.anchor.set(0.5, 0);
    this.container.addChild(this.countText);

    // Multiplier
    this.multiplierText = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: 'Inter, Arial, sans-serif',
        fontSize: 10,
        fontWeight: '600',
        fill: '#fbbf24', // amber-400
        align: 'center',
      }),
    });
    this.multiplierText.anchor.set(0.5, 0);
    this.container.addChild(this.multiplierText);

    this.container.visible = false;
  }

  show(remaining: number, multiplier: number): void {
    this.countText.text = String(remaining);
    this.multiplierText.text = multiplier > 0 ? `x${multiplier}` : '';
    this.multiplierText.visible = multiplier > 0;

    this.layoutBadge();

    this.container.visible = true;
    this.container.alpha = 1;
  }

  hide(): void {
    gsap.killTweensOf(this.container);
    this.container.visible = false;
  }

  destroy(): void {
    gsap.killTweensOf(this.container);
    this.bg.destroy();
    this.titleText.destroy();
    this.countText.destroy();
    this.multiplierText.destroy();
    this.container.destroy({ children: true });
  }

  private layoutBadge(): void {
    const paddingX = 12;
    const paddingY = 6;

    // Stack: title, count, multiplier
    let y = paddingY;
    this.titleText.position.set(0, y);
    y += 14;
    this.countText.position.set(0, y);
    y += 22;
    if (this.multiplierText.visible) {
      this.multiplierText.position.set(0, y);
      y += 14;
    }
    y += paddingY;

    // Measure width from the widest text
    const maxWidth = Math.max(
      this.titleText.width,
      this.countText.width,
      this.multiplierText.visible ? this.multiplierText.width : 0
    );

    const bgWidth = maxWidth + paddingX * 2;
    const bgHeight = y;

    // Draw purple-tinted rounded rect background
    this.bg.clear();
    this.bg.roundRect(-bgWidth / 2, 0, bgWidth, bgHeight, 8)
      .fill({ color: 0x7c3aed, alpha: 0.9 }); // purple-600

    // Add subtle border
    this.bg.roundRect(-bgWidth / 2, 0, bgWidth, bgHeight, 8)
      .stroke({ color: 0xa855f7, width: 1, alpha: 0.6 }); // purple-500
  }
}
